/**
 * /app/dashboards/:id — render a single dashboard with View / Edit modes.
 *
 *  - View mode (default) renders the dashboard read-only.
 *  - Edit mode (toggle in header, hidden for pack-shipped) enables drag /
 *    resize, shows a palette to add widgets, and a delete-on-hover X on
 *    each widget. In live mode the dashboards store debounces a PATCH to
 *    /v1/iot/dashboards/{id} (create/update/delete all round-trip); stub
 *    mode persists to localStorage. Save waits for the server and leaves
 *    edit mode only when it has the board (2026-10-27, #1675).
 *
 * The full Entity Alias + Tag Keys data-source picker (specced in
 * docs/06 § 5) lands in B0.20 once Arun's backend bindings are in.
 *
 * The header offers "Ask" (2026-09-28; lib/copilot/ask): the consultant opens with a question about the board, which
 * it names; the top bar's button opens with the same context.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";
import { AlertTriangle, ArrowLeft, Database, Edit3, Eye, Lock, Plus, Save, Settings, Share2, X } from "lucide-react";
import { PageContainer } from "@/routes/_layout";
import { PageHeader } from "@/lib/ui/page-header";
import { Badge } from "@/lib/ui/badge";
import { Button } from "@/lib/ui/button";
import { Card } from "@/lib/ui/card";
import { EmptyState } from "@/lib/ui/empty-state";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/lib/ui/tooltip";
import { ErrorBoundary } from "@/lib/ui/error-boundary";
import { cn } from "@/lib/ui/cn";
import { DashboardRenderer } from "@/lib/dashboard/DashboardRenderer";
import { WIDGET_REGISTRY, type WidgetRegistryEntry } from "@/lib/dashboard/widget-registry";
import { EntityAliasPicker } from "@/lib/dashboard/EntityAliasPicker";
import { useDashboards } from "@/lib/dashboard/store";
import { BoardSkeleton } from "@/lib/widgets/ChartSkeleton";
import {
  addWidgetAndSelect,
  deleteWidgetWithUndo,
  escapeClosesPanel,
  modifiedLabel,
  saveBoardEdit,
  scrollWidgetIntoView,
} from "@/lib/dashboard/edit-actions";
import type { LayoutWrite } from "@/lib/dashboard/layout-save";
import { writeErrorMessage } from "@/lib/api";
import { axisBoundsShown, axisPatch } from "@/lib/dashboard/axis-units";
import { useAxisUnits } from "@/lib/dashboard/use-axis-units";
import { useOrg } from "@/lib/org-context";
import { useToast } from "@/lib/ui/toast";
import { usePageCopilotContext } from "@/lib/copilot/page-context";
import { aboutFor } from "@/lib/copilot/ask";
import { AskCopilot } from "@/components/copilot/AskCopilot";
import type {
  DashboardConfig,
  DataSource,
  WidgetInstance,
  WidgetType,
} from "@/lib/dashboard/types";

/**
 * One editor per board: the route keeps this page mounted when only the board's id changes (a link from the consultant
 * or the top bar), and the edit state of the board left (edit mode, its snapshot, what is selected) then held on the
 * next one, so Exit there wrote the first board's snapshot over the second (editor review, 2026-10-30).
 */
export function DashboardView() {
  const { id = "" } = useParams<{ id: string }>();
  return <DashboardBoard key={id} />;
}

function DashboardBoard() {
  const { id = "" } = useParams<{ id: string }>();
  const [params, setParams] = useSearchParams();
  const dashboards = useDashboards();
  const { can } = useOrg();
  // Edit-mode toggle + save/add-widget/delete are dashboard authoring →
  // dashboards.write. View + Share stay available to any reader.
  const canWrite = can("dashboards.write");
  const toast = useToast();
  const dashboard = dashboards.byId(id) ?? null;

  // The consultant is asked about the board by its name (lib/copilot/ask); the top bar's button opens with it too.
  const about = dashboard ? aboutFor({ page: "dashboard", label: dashboard.name }) : undefined;
  usePageCopilotContext({
    route: `/app/dashboards/${id}`,
    label: dashboard?.name ?? "Dashboard",
    currentDashboardId: id,
    about,
  });

  // Snapshot taken on enter-edit-mode so Exit-without-Save can revert.
  const [snapshot, setSnapshot] = useState<DashboardConfig | null>(null);
  const [editMode, setEditMode] = useState(false);
  const [dirty, setDirty] = useState(false);
  const [selectedWidgetId, setSelectedWidgetId] = useState<string | null>(null);
  // Save is out to the server: the button waits, and Exit stays put so it cannot revert a board mid-write.
  const [saving, setSaving] = useState(false);
  // A widget just added: the page scrolls to it once it is on the grid (#1676).
  const [scrollToWidgetId, setScrollToWidgetId] = useState<string | null>(null);
  useEffect(() => {
    if (scrollToWidgetId) scrollWidgetIntoView(document, scrollToWidgetId);
  }, [scrollToWidgetId]);

  // Auto-open edit mode when `?edit=1` is on the URL (used by the
  // "Create dashboard" modal to drop the user straight into the editor).
  useEffect(() => {
    if (params.get("edit") === "1" && dashboard && dashboard.source !== "pack") {
      setEditMode(true);
      setSnapshot(JSON.parse(JSON.stringify(dashboard)));
      params.delete("edit");
      setParams(params, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dashboard?.id]);

  // A delete's toast offers Undo after this render is gone, so it reaches the store as it is then, not as it was at the
  // delete (a store action from that render would rebuild the board from its old copy). The toast is closed when the
  // edit ends, so its Undo never touches a board out of edit mode.
  const storeRef = useRef(dashboards);
  useEffect(() => {
    storeRef.current = dashboards;
  });
  const undoToastRef = useRef<number | null>(null);
  const closeUndo = () => {
    if (undoToastRef.current !== null) toast.dismiss(undoToastRef.current);
    undoToastRef.current = null;
  };
  // Leaving the board (another page, or another board: one editor per board) ends the edit too.
  useEffect(() => {
    const undoToast = undoToastRef;
    return () => {
      if (undoToast.current !== null) toast.dismiss(undoToast.current);
    };
  }, [toast]);

  // Escape closes the settings panel; not while a field has the keys (the rename's own Escape ends the rename).
  useEffect(() => {
    if (!editMode || !selectedWidgetId) return;
    const onKey = (e: KeyboardEvent) => {
      if (escapeClosesPanel(e)) setSelectedWidgetId(null);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [editMode, selectedWidgetId]);

  if (!dashboard) {
    if (dashboards.loading) {
      return (
        <PageContainer className="max-w-7xl">
          <BoardSkeleton />
        </PageContainer>
      );
    }
    return (
      <PageContainer>
        <EmptyState
          title="Dashboard not found"
          description={`No dashboard with id "${id}" was found. It may have been deleted, or the link is from a different tenant.`}
          cta={
            <Button asChild>
              <Link to="/app/dashboards">
                <ArrowLeft className="mr-1.5 size-4" />
                Back to dashboards
              </Link>
            </Button>
          }
        />
      </PageContainer>
    );
  }

  const sourceBadge = {
    my: { label: "My dashboard", variant: "default" as const },
    pack: { label: "Pack dashboard", variant: "info" as const },
    shared: { label: "Shared with you", variant: "secondary" as const },
  }[dashboard.source];

  const isReadOnly = dashboard.source === "pack";

  // Every handler delegates to the DashboardsProvider — writes are
  // immediate + persisted. The `dirty` flag is kept as a UX affordance
  // (Save button labelled "Save" vs "Saved") even though Phase 0 saves
  // are no-ops against the local store. Phase 1 swaps this for a real
  // POST to /v1/iot/dashboards/{id}.

  // A drop saves every widget it moved at once (layout-save.ts).
  const handleLayoutSave = (writes: LayoutWrite[]) => {
    dashboards.updateLayouts(id, writes);
    setDirty(true);
  };

  // A delete says what went, and can be undone from its toast until the edit ends.
  const handleDelete = (widgetId: string) => {
    const deleted = deleteWidgetWithUndo({
      dashboard,
      widgetId,
      removeWidget: (wid) => dashboards.removeWidget(id, wid),
      addWidget: (widget) => {
        const store = storeRef.current;
        if (store.byId(id)?.widgets.some((w) => w.id === widget.id)) return;
        store.addWidget(id, widget);
      },
      markDirty: () => setDirty(true),
    });
    if (!deleted) return;
    if (selectedWidgetId === widgetId) setSelectedWidgetId(null);
    closeUndo();
    const name = deleted.widget.title || WIDGET_REGISTRY[deleted.widget.type]?.name || "widget";
    undoToastRef.current = toast.push("info", `Deleted "${name}"`, {
      action: {
        label: "Undo",
        onClick: () => {
          undoToastRef.current = null;
          deleted.undo();
        },
      },
      durationMs: 8000,
    });
  };

  const handleDataSourceChange = (widgetId: string, dataSource: DataSource) => {
    dashboards.setWidgetDataSource(id, widgetId, dataSource);
    setDirty(true);
  };

  /**
   * Apply a partial update to a widget (used by both the config panel's
   * Appearance tab and the inline rename in the widget header).
   */
  const handleWidgetUpdate = (widgetId: string, patch: Partial<WidgetInstance>) => {
    dashboards.updateWidget(id, widgetId, patch);
    setDirty(true);
  };

  const handleAddWidget = (type: WidgetType) => {
    const entry = WIDGET_REGISTRY[type];
    if (!entry) return;
    // The new widget is selected, so the open config panel edits it and never the widget selected before (#1676).
    addWidgetAndSelect({
      dashboard,
      type,
      entry,
      nowMs: Date.now(),
      addWidget: (widget) => dashboards.addWidget(id, widget),
      select: (widgetId) => {
        setSelectedWidgetId(widgetId);
        setScrollToWidgetId(widgetId);
      },
      markDirty: () => setDirty(true),
    });
  };

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    // Save waits for the server. On success the edit is over; on failure the editor stays in edit mode with the edit
    // intact (and the snapshot, so Exit can still revert), and says why (#1675).
    await saveBoardEdit({
      save: () => dashboards.save(id),
      onSaved: () => {
        closeUndo();
        setSnapshot(null);
        setDirty(false);
        setEditMode(false);
        setSelectedWidgetId(null);
        toast.push("ok", `Saved "${dashboard.name}"`);
      },
      onFailed: (err) =>
        toast.push("error", writeErrorMessage(err, `Couldn't save "${dashboard.name}". Your changes are still here.`)),
    });
    setSaving(false);
  };

  const handleExit = () => {
    closeUndo();
    setEditMode(false);
    setSelectedWidgetId(null);
    if (dirty && snapshot) {
      // Restore the pre-edit snapshot — discards unsaved changes.
      dashboards.update(id, snapshot);
      toast.push("info", "Discarded unsaved changes");
    }
    setSnapshot(null);
    setDirty(false);
  };

  return (
    <TooltipProvider delayDuration={250}>
      <PageContainer className="max-w-7xl">
        <PageHeader
          breadcrumb={
            <>
              <Link to="/app" className="hover:text-on-surface">
                App
              </Link>
              <span aria-hidden>/</span>
              <Link to="/app/dashboards" className="hover:text-on-surface">
                Dashboards
              </Link>
              <span aria-hidden>/</span>
              <span className="text-on-surface">{dashboard.name}</span>
              {editMode && (
                <Badge variant="warning" className="ml-1">
                  Editing
                </Badge>
              )}
            </>
          }
          title={dashboard.name}
          description={dashboard.description}
          actions={
            <>
              <Badge variant={sourceBadge.variant}>{sourceBadge.label}</Badge>
              {dashboard.pack && (
                <Badge variant="secondary" className="font-mono">
                  {dashboard.pack}
                </Badge>
              )}
              {isReadOnly && (
                <Badge variant="warning" className="flex items-center gap-1">
                  <Lock className="size-3" />
                  Read-only
                </Badge>
              )}
              {!editMode && (
                <>
                  {about && (
                    <AskCopilot
                      question={`What stands out on the ${dashboard.name} board right now, and what should we check first?`}
                      about={about}
                    />
                  )}
                  <Button size="sm" variant="outline">
                    <Share2 className="mr-1.5 size-4" />
                    Share
                  </Button>
                  {canWrite && (
                    <Tooltip>
                      <TooltipTrigger asChild>
                        <span tabIndex={-1}>
                          <Button
                            size="sm"
                            disabled={isReadOnly}
                            onClick={() => {
                              setSnapshot(JSON.parse(JSON.stringify(dashboard)));
                              setEditMode(true);
                            }}
                          >
                            <Edit3 className="mr-1.5 size-4" />
                            {isReadOnly ? "Clone to edit" : "Edit"}
                          </Button>
                        </span>
                      </TooltipTrigger>
                      <TooltipContent>
                        {isReadOnly
                          ? "Pack dashboards are read-only. Clone to make edits."
                          : "Drag, resize and add widgets."}
                      </TooltipContent>
                    </Tooltip>
                  )}
                </>
              )}
              {editMode && (
                <>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      {/* A disabled button gets no hover: the span carries the hint. */}
                      <span tabIndex={-1}>
                        <Button size="sm" variant="outline" onClick={handleExit} disabled={saving}>
                          <Eye className="mr-1.5 size-4" />
                          Exit
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {saving
                        ? "Wait for the save to finish."
                        : dirty
                          ? "Leave edit mode and discard the changes made since Edit."
                          : "Leave edit mode."}
                    </TooltipContent>
                  </Tooltip>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <span tabIndex={-1}>
                        <Button size="sm" onClick={handleSave} disabled={!dirty || saving}>
                          <Save className="mr-1.5 size-4" />
                          {saving ? "Saving…" : "Save"}
                        </Button>
                      </span>
                    </TooltipTrigger>
                    <TooltipContent>
                      {saving
                        ? "Saving the board."
                        : dirty
                          ? "Save the board and leave edit mode."
                          : "Nothing to save yet: move, resize, add or change a widget first."}
                    </TooltipContent>
                  </Tooltip>
                </>
              )}
            </>
          }
        />

        <div className="flex flex-wrap items-center gap-3 text-xs text-on-surface-muted">
          <span>
            Owner <span className="text-on-surface">{dashboard.owner}</span>
          </span>
          <span>
            Last modified{" "}
            <span className="text-on-surface">{modifiedLabel(dashboard.updatedAt)}</span>
          </span>
          <span>
            {dashboard.widgets.length} {dashboard.widgets.length === 1 ? "widget" : "widgets"}
          </span>
        </div>

        {editMode && <WidgetPalette onAdd={handleAddWidget} />}

        {/* The settings panel sits beside the board (minmax(0,1fr): Tailwind 4 reads no comma in a class, so the old
            [1fr,320px] put the panel under the whole board, off screen) and stays in view while the board scrolls. */}
        <div
          className={cn("grid gap-4", editMode && selectedWidgetId ? "lg:grid-cols-[minmax(0,1fr)_320px]" : undefined)}
        >
          {/* The grid itself (moving, resizing, its layout) is outside every widget's boundary: a fault there says so
              here, and in edit mode Exit takes the board back to how it was at Edit. */}
          <ErrorBoundary
            label={`The board "${dashboard.name}"`}
            resetKey={editMode ? "edit" : "view"}
            fallback={(error, retry) => (
              <Card role="alert" className="flex flex-col items-start gap-2 p-4 text-sm">
                <span className="flex items-center gap-1.5 font-medium text-on-surface">
                  <AlertTriangle aria-hidden className="size-4 text-warning" />
                  This board could not be drawn.
                </span>
                <span className="break-words text-xs text-on-surface-muted">{error.message}</span>
                <div className="flex gap-1.5">
                  <Button size="sm" variant="outline" onClick={retry}>
                    Try again
                  </Button>
                  {editMode && (
                    <Button size="sm" variant="outline" onClick={handleExit} disabled={saving}>
                      Exit edit mode
                    </Button>
                  )}
                </div>
              </Card>
            )}
          >
            <DashboardRenderer
              dashboard={dashboard}
              editMode={editMode}
              onLayoutSave={handleLayoutSave}
              onDeleteWidget={handleDelete}
              onSelectWidget={(id) => setSelectedWidgetId(id)}
              selectedWidgetId={selectedWidgetId}
              onWidgetUpdate={handleWidgetUpdate}
            />
          </ErrorBoundary>
          {editMode && selectedWidgetId && (
            <ErrorBoundary
              label="The widget settings panel"
              resetKey={selectedWidgetId}
              fallback={(error, retry) => (
                <Card role="alert" className="flex flex-col items-start gap-2 self-start p-3 text-xs">
                  <span className="flex items-center gap-1.5 font-medium text-on-surface">
                    <AlertTriangle aria-hidden className="size-3.5 text-warning" />
                    The settings of this widget could not be drawn.
                  </span>
                  <span className="break-words text-on-surface-muted">{error.message}</span>
                  <div className="flex gap-1.5">
                    <Button size="sm" variant="outline" onClick={retry}>
                      Try again
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => setSelectedWidgetId(null)}>
                      Close
                    </Button>
                  </div>
                </Card>
              )}
            >
              <WidgetConfigPanel
                widget={dashboard.widgets.find((w) => w.id === selectedWidgetId)}
                onClose={() => setSelectedWidgetId(null)}
                onDataSourceChange={(ds) => handleDataSourceChange(selectedWidgetId, ds)}
                onUpdate={(patch) => handleWidgetUpdate(selectedWidgetId, patch)}
              />
            </ErrorBoundary>
          )}
        </div>
      </PageContainer>
    </TooltipProvider>
  );
}

// The Behavior tab is gone (2026-10-30): it held only a note that its settings "ship with Phase 1". A widget's time
// window is the range picker on the widget itself.
type ConfigTab = "data" | "appearance";

/**
 * Widget types whose value (Y) axis range is user-configurable. These map
 * to chart components that accept `yMin`/`yMax` (see widget-registry.tsx).
 */
const AXIS_RANGE_WIDGET_TYPES: ReadonlySet<WidgetType> = new Set<WidgetType>([
  "timeseries",
  "gauge",
  "histogram",
  "spc",
  "scatter",
]);

function WidgetConfigPanel({
  widget,
  onClose,
  onDataSourceChange,
  onUpdate,
}: {
  widget: WidgetInstance | undefined;
  onClose: () => void;
  onDataSourceChange: (ds: DataSource) => void;
  onUpdate: (patch: Partial<WidgetInstance>) => void;
}) {
  const [tab, setTab] = useState<ConfigTab>("data");
  // A widget picked brings the panel into view: beside the board it already is (nothing moves); under it, on a narrow
  // window, the page scrolls to it.
  const panelRef = useRef<HTMLDivElement>(null);
  const widgetId = widget?.id;
  useEffect(() => {
    if (widgetId) panelRef.current?.scrollIntoView?.({ block: "nearest" });
  }, [widgetId]);
  if (!widget) return null;
  const entry = WIDGET_REGISTRY[widget.type];

  const updateConfig = (patch: Record<string, unknown>) => {
    onUpdate({ config: { ...(widget.config ?? {}), ...patch } });
  };

  return (
    <Card
      ref={panelRef}
      className="self-start lg:sticky lg:top-4 lg:max-h-[calc(100vh-2rem)] lg:overflow-y-auto"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-3 py-2">
        <div className="flex min-w-0 items-center gap-2">
          {entry && <entry.icon className="size-4 shrink-0 text-brand" />}
          <span className="truncate text-sm font-semibold text-on-surface">
            {widget.title || entry?.name || widget.type}
          </span>
        </div>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button variant="ghost" size="icon" onClick={onClose} aria-label="Close panel">
              <X className="size-3.5" />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="left">Close the settings (Esc)</TooltipContent>
        </Tooltip>
      </div>

      {/* Tabs */}
      <div className="flex items-stretch border-b border-border bg-surface-elevated/40">
        <ConfigTabButton
          active={tab === "data"}
          onClick={() => setTab("data")}
          icon={Database}
          label="Data"
        />
        <ConfigTabButton
          active={tab === "appearance"}
          onClick={() => setTab("appearance")}
          icon={Settings}
          label="Appearance"
        />
      </div>

      {/* Body */}
      <div className="flex flex-col gap-4 p-3">
        {tab === "data" && (
          <>
            {/* Keyed by the widget so a widget picked next starts the picker at its own binding, not the last one's step. */}
            <EntityAliasPicker
              key={widget.id}
              value={widget.dataSource}
              onChange={onDataSourceChange}
              slots={entry?.accepts ?? []}
              widgetName={entry?.name ?? "Widget"}
            />
            <div className="border-t border-border pt-3">
              <h3 className="mb-1 text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
                About
              </h3>
              <p className="text-[11px] text-on-surface-muted">
                {entry?.description ?? "—"}
              </p>
            </div>
          </>
        )}

        {tab === "appearance" && (
          <div className="flex flex-col gap-3">
            <LabelledField label="Title">
              <input
                type="text"
                value={widget.title ?? ""}
                onChange={(e) => onUpdate({ title: e.target.value })}
                placeholder={entry?.name ?? "Widget title"}
                className="h-8 w-full rounded-md border border-border bg-surface px-2 text-xs text-on-surface focus:border-brand focus:outline-none"
              />
            </LabelledField>
            <LabelledField label="Subtitle">
              <input
                type="text"
                value={widget.subtitle ?? ""}
                onChange={(e) => onUpdate({ subtitle: e.target.value })}
                placeholder="Optional secondary line"
                className="h-8 w-full rounded-md border border-border bg-surface px-2 text-xs text-on-surface focus:border-brand focus:outline-none"
              />
            </LabelledField>
            {/* Decimals, Show legend and Show grid are gone (2026-10-30): no widget read them, so they changed nothing. */}
            {AXIS_RANGE_WIDGET_TYPES.has(widget.type) && (
              <AxisRangeField config={widget.config} dataSource={widget.dataSource} onChange={(next) => updateConfig(next)} />
            )}
          </div>
        )}

      </div>
    </Card>
  );
}

function ConfigTabButton({
  active,
  onClick,
  icon: Icon,
  label,
}: {
  active: boolean;
  onClick: () => void;
  icon: typeof Database;
  label: string;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        "flex flex-1 items-center justify-center gap-1.5 px-3 py-2 text-[11px] font-medium transition-colors",
        active
          ? "border-b-2 border-brand text-brand"
          : "border-b-2 border-transparent text-on-surface-muted hover:text-on-surface",
      )}
    >
      <Icon className="size-3.5" />
      {label}
    </button>
  );
}

function LabelledField({
  label,
  children,
}: {
  label: string;
  children: React.ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
        {label}
      </span>
      {children}
    </label>
  );
}

/**
 * Axis-range control for chart widgets. Defaults to **Auto** (the axis fits
 * the data). Switching to **Fixed** reveals Min / Max inputs that write
 * `config.yMin` / `config.yMax`; switching back to Auto clears both so the
 * chart reverts to autorange. One-sided fixing is allowed — leaving either
 * input blank keeps that bound on auto.
 *
 * The bounds are typed in the unit the chart shows its data in, and saved with it (`config.yUnit`), so the chart keeps
 * them on its data whatever units the org reads in (#1190 review MEDIUM 2).
 */
function AxisRangeField({
  config,
  dataSource,
  onChange: save,
}: {
  config: Record<string, unknown> | undefined;
  dataSource: DataSource | undefined;
  onChange: (patch: { yMin?: number; yMax?: number; yUnit?: string }) => void;
}) {
  const units = useAxisUnits(dataSource);
  const { yMin, yMax } = axisBoundsShown(config, units.stored, units.shown);
  const onChange = (next: { yMin?: number; yMax?: number }) => save(axisPatch(next, units.shown));
  const unitNote = units.shown ? ` (${units.shown})` : "";
  const isFixed = yMin !== undefined || yMax !== undefined;

  const setMode = (fixed: boolean) => {
    if (fixed) {
      // Seed sensible defaults only when coming from Auto with nothing set.
      if (!isFixed) onChange({ yMin: 0, yMax: 100 });
    } else {
      // Back to autorange — drop both bounds from config.
      onChange({ yMin: undefined, yMax: undefined });
    }
  };

  const parse = (raw: string): number | undefined => {
    if (raw.trim() === "") return undefined;
    const n = Number(raw);
    return Number.isFinite(n) ? n : undefined;
  };

  return (
    <div className="flex flex-col gap-2">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
          Axis range
        </span>
        <div className="flex overflow-hidden rounded-md border border-border">
          {[
            { key: "auto", label: "Auto" },
            { key: "fixed", label: "Fixed" },
          ].map((opt) => {
            const active = opt.key === "fixed" ? isFixed : !isFixed;
            return (
              <button
                key={opt.key}
                type="button"
                onClick={() => setMode(opt.key === "fixed")}
                className={cn(
                  "px-2.5 py-1 text-[11px] font-medium transition-colors",
                  active
                    ? "bg-brand text-brand-foreground"
                    : "bg-surface text-on-surface-muted hover:text-on-surface",
                )}
              >
                {opt.label}
              </button>
            );
          })}
        </div>
      </div>
      {isFixed && (
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <LabelledField label={`Min${unitNote}`}>
              <input
                type="number"
                value={yMin ?? ""}
                onChange={(e) => onChange({ yMin: parse(e.target.value), yMax })}
                placeholder="auto"
                className="h-8 w-full rounded-md border border-border bg-surface px-2 text-xs text-on-surface focus:border-brand focus:outline-none"
              />
            </LabelledField>
          </div>
          <div className="flex-1">
            <LabelledField label={`Max${unitNote}`}>
              <input
                type="number"
                value={yMax ?? ""}
                onChange={(e) => onChange({ yMin, yMax: parse(e.target.value) })}
                placeholder="auto"
                className="h-8 w-full rounded-md border border-border bg-surface px-2 text-xs text-on-surface focus:border-brand focus:outline-none"
              />
            </LabelledField>
          </div>
        </div>
      )}
    </div>
  );
}

/**
 * Palette section labels + display order. Keeps related widgets grouped under
 * a readable heading (instead of the raw category id) and gives the sections a
 * deterministic order so the event/status/categorical widgets are easy to find
 * rather than scattered in object-key order. Any category missing here falls
 * back to its raw id and sorts last.
 */
const PALETTE_CATEGORY_ORDER: WidgetRegistryEntry["category"][] = [
  "kpi",
  "timeseries",
  "categorical",
  "statistical",
  "event",
  "table",
  "industrial",
  "spatial",
  "content",
];

const PALETTE_CATEGORY_LABEL: Record<WidgetRegistryEntry["category"], string> = {
  kpi: "KPI & status",
  timeseries: "Time-series",
  categorical: "Distributions",
  statistical: "Statistical",
  event: "Events & alarms",
  table: "Tables",
  industrial: "Foundry pack",
  spatial: "Spatial",
  content: "Content",
};

function WidgetPalette({ onAdd }: { onAdd: (type: WidgetType) => void }) {
  const groups = useMemo(() => {
    const out: Record<string, WidgetType[]> = {};
    for (const [type, entry] of Object.entries(WIDGET_REGISTRY)) {
      const cat = entry.category;
      out[cat] = out[cat] ?? [];
      out[cat].push(type as WidgetType);
    }
    // Order sections deterministically (PALETTE_CATEGORY_ORDER first, then any
    // others) so every widget — including event-feed, status-grid and pareto —
    // has a stable, findable home in the palette.
    return Object.entries(out).sort(([a], [b]) => {
      const ia = PALETTE_CATEGORY_ORDER.indexOf(a as WidgetRegistryEntry["category"]);
      const ib = PALETTE_CATEGORY_ORDER.indexOf(b as WidgetRegistryEntry["category"]);
      return (ia === -1 ? 99 : ia) - (ib === -1 ? 99 : ib);
    });
  }, []);

  return (
    <Card className="overflow-hidden">
      {/* How to arrange the board, where it is read: above it, not under a board that can run off the screen. */}
      <div className="flex flex-wrap items-center justify-between gap-x-4 gap-y-1 border-b border-border bg-surface-muted/40 px-3 py-2">
        <span className="flex items-center gap-2 text-xs font-semibold uppercase tracking-wide text-on-surface-muted">
          <Plus className="size-4" />
          Add widget
        </span>
        <span className="text-[11px] text-on-surface-muted">
          Drag a widget to move it, drag its bottom-right corner to resize it, click it to set its data.
        </span>
      </div>
      <div className="flex flex-wrap gap-4 p-3">
        {groups.map(([cat, types]) => (
          <div key={cat} className="flex min-w-32 flex-col gap-1.5">
            <span className="text-[10px] font-semibold uppercase tracking-wide text-on-surface-muted">
              {PALETTE_CATEGORY_LABEL[cat as WidgetRegistryEntry["category"]] ?? cat}
            </span>
            <div className="flex flex-wrap gap-1">
              {types.map((type) => {
                const entry = WIDGET_REGISTRY[type];
                const Icon = entry.icon;
                return (
                  <Tooltip key={type}>
                    <TooltipTrigger asChild>
                      <button
                        type="button"
                        onClick={() => onAdd(type)}
                        className={cn(
                          "flex items-center gap-1.5 rounded-md border border-border bg-surface px-2 py-1 text-xs text-on-surface transition-colors",
                          "hover:border-brand hover:bg-brand-muted hover:text-brand",
                        )}
                      >
                        <Icon className="size-3.5" />
                        {entry.name}
                      </button>
                    </TooltipTrigger>
                    <TooltipContent className="max-w-[18rem]">
                      <p className="font-semibold">Add a {entry.name}</p>
                      <p className="mt-0.5 text-on-surface-muted">{entry.description}</p>
                    </TooltipContent>
                  </Tooltip>
                );
              })}
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}
